import type { Action, Thunk } from 'easy-peasy';
import { action, thunk } from 'easy-peasy';
import type { StoreModel } from '../..';
import parseStatusCode from '../../errors/parseStatusCode';
import type { Injections } from '../../services/injections';
import type { AugmentationSet } from '../../services/FP_FulfillmentContextService/types';
import type { ApplicationErrorOptions } from '../../../types';
import type {
  BulkAssetLinkResults,
  BulkLinkAugmentationSetToContextsPayload,
} from '../../services/UIOrchestrationService/types';

export interface AugmentationSetsModel {
  augmentationSetsMap: Record<string, AugmentationSet>;
  setAugmentationSets: Action<AugmentationSetsModel, AugmentationSet[]>;
  setAugmentationSet: Action<AugmentationSetsModel, AugmentationSet>;
  fetchAugmentationSetsLoading: boolean;
  setFetchAugmentationSetsLoading: Action<AugmentationSetsModel, boolean>;
  fetchAugmentationSets: Thunk<AugmentationSetsModel, void, Injections, StoreModel, Promise<void>>;
  bulkLinkAugmentationSetToContexts: Thunk<
    AugmentationSetsModel,
    BulkLinkAugmentationSetToContextsPayload,
    Injections,
    StoreModel,
    Promise<BulkAssetLinkResults>
  >;
  fetchLabelAugmentationSetsFailure: Action<AugmentationSetsModel, ApplicationErrorOptions>;
}

export const augmentationSetsModel: AugmentationSetsModel = {
  augmentationSetsMap: {},
  setAugmentationSets: action((state, augmentationSets) => {
    const augmentationSetsMap = augmentationSets.reduce<Record<string, AugmentationSet>>((acc, curr) => {
      acc[curr.augmentationSetId] = curr;
      return acc;
    }, {});
    state.augmentationSetsMap = augmentationSetsMap;
  }),
  setAugmentationSet: action((state, augmentationSet) => {
    state.augmentationSetsMap[augmentationSet.augmentationSetId] = augmentationSet;
  }),
  fetchAugmentationSetsLoading: false,
  setFetchAugmentationSetsLoading: action((state, isLoading) => {
    state.fetchAugmentationSetsLoading = isLoading;
  }),
  fetchAugmentationSets: thunk(async (actions, payload, { injections, getState }) => {
    try {
      actions.setFetchAugmentationSetsLoading(true);
      const { fulfillmentContextService } = injections;
      const response = await fulfillmentContextService.fetchAugmentationSets();
      actions.setAugmentationSets(response);
      actions.setFetchAugmentationSetsLoading(false);
      return response;
    } catch (error) {
      actions.fetchLabelAugmentationSetsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      return error;
    }
  }),
  fetchLabelAugmentationSetsFailure: action((state, error) => {
    console.error(error);
  }),
  bulkLinkAugmentationSetToContexts: thunk(async (actions, payload, { injections, getState }) => {
    try {
      const { UIOrchestrationService } = injections;
      const response = await UIOrchestrationService.bulkLinkAugmentationSetToContexts(payload);
      return response;
    } catch (error) {
      actions.fetchLabelAugmentationSetsFailure({
        error,
        httpCode: parseStatusCode(error),
        asNotification: false,
        message: error.message || 'An error occurred, unable to fetch asset details',
      });
      throw error;
    }
  }),
};

export default augmentationSetsModel;
